<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { InputSelect, InputCheckboxTrueFalse } from '@/components/inputs';

const props = defineProps({
  modelValue: {
    type: [String, Boolean],
    default: false,
  },
  defaultVal: {
    type: String,
    required: true,
  },
  selectOptions: {
    type: Array,
    required: true,
  },
  optionTrue: {
    type: String,
    required: true,
  },
  optionFalse: {
    type: String,
    required: true,
  },
  mainColor: {
    type: String,
    required: true,
  },
  backgroundColor: {
    type: String,
    required: true,
  },
  textFont: {
    type: String,
    default: 'null',
  },
  fancy: {
    type: Boolean,
    default: false,
  },
  borderRadius: {
    type: String,
    default: '20px',
  },
});

const emits = defineEmits(['update:modelValue']);

function setCheckboxVal() {
  if (props.modelValue && props.modelValue !== '') {
    return true;
  }

  return false;
}

const checkboxVal = ref(setCheckboxVal());

const model = computed({
  get() {
    return props.modelValue;
  },
  set(val) {
    emits('update:modelValue', val);
  },
});

watch(checkboxVal, (val) => {
  if (val === false) {
    emits('update:modelValue', false);
  } else if (props.defaultVal) {
    emits('update:modelValue', props.defaultVal);
  } else {
    emits('update:modelValue', props.selectOptions[0]);
  }
});

onMounted(() => {
  if (!checkboxVal.value) {
    model.value = false;
  }
});
</script>

<template>
  <div class="input">
    <p v-if="$slots.default" class="custom-font">
      <slot></slot>
    </p>

    <InputCheckboxTrueFalse
      v-model="checkboxVal"
      :option-true="optionTrue"
      :option-false="optionFalse"
      :main-color="mainColor"
      :background-color="backgroundColor"
      :text-font="textFont"
      :fancy="fancy"
    />
    <transition name="fade" mode="out-in">
      <div
        v-show="checkboxVal"
        class="select"
        :class="{ 'select-right': fancy }"
      >
        <InputSelect
          v-model="model"
          :select-options="selectOptions"
          :main-color="mainColor"
          :background-color="backgroundColor"
          :text-font="textFont"
          :border-radius="borderRadius"
        >
          <p class="label">
            {{
              $t(
                'appCore.apps.components.inputs.inputCheckboxTrueFalseSelect.selectLabel',
              )
            }}
          </p>
        </InputSelect>
      </div>
    </transition>
  </div>
</template>

<style lang="scss">
/* stylelint-disable selector-class-pattern */
.rsvp-guest-checkbox-true-false > .q-checkbox__inner,
.rsvp-guest-checkbox-true-false > .q-checkbox__inner--truthy {
  color: v-bind(mainColor) !important;
}
/* stylelint-enable selector-class-pattern */
</style>

<style lang="scss" scoped>
@import '@/assets/styleConstans';

.checkbox {
  border: none;
  align-items: center;
  justify-content: center;
  background-color: v-bind(backgroundColor);
  width: auto;
  margin: auto;
}

.input {
  display: flex;
  flex-direction: column;
  gap: 15px;

  .custom-font {
    font-family: v-bind(textFont), 'Petit Formal Script', serif;
    font-style: normal;
    font-weight: 400;
  }
}

.label-right {
  flex-direction: row-reverse;
  justify-content: center;
  align-items: center;

  .checkbox {
    padding: 10px;
  }
}

.select {
  display: flex;
  flex-direction: column;
  gap: 15px;

  .label {
    font-family: v-bind(textFont), 'Petit Formal Script', serif;
    text-align: center;
    font-size: 1.6em;

    @media screen and (max-width: $media-phone) {
      font-size: 1.25em;
    }
  }
}

.select-right p {
  text-align: left;
}
</style>
